import React, { useState, useEffect } from 'react';
import { CheckCircleIcon, ExclamationTriangleIcon, InformationCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';

/**
 * Global Toast Notification System
 * Renders above all modals (z-9999)
 * Auto-dismisses after 4 seconds
 */

let toastQueue = [];
let toastListeners = [];

const subscribeToToasts = (callback) => {
  toastListeners.push(callback);
  return () => {
    toastListeners = toastListeners.filter(l => l !== callback);
  };
};

const notifyToastListeners = () => {
  toastListeners.forEach(listener => listener([...toastQueue]));
};

let toastCounter = 0;
export const showToast = (type, message, duration = 4000) => {
  const id = `${Date.now()}_${++toastCounter}`;
  const toast = { id, type, message };
  
  toastQueue.push(toast);
  notifyToastListeners();
  
  setTimeout(() => {
    toastQueue = toastQueue.filter(t => t.id !== id);
    notifyToastListeners();
  }, duration);
};

const Toast = ({ toast, onDismiss }) => {
  const getIcon = () => {
    switch (toast.type) {
      case 'success':
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      case 'error':
        return <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />;
      case 'warning':
        return <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500" />;
      case 'info':
      default:
        return <InformationCircleIcon className="w-5 h-5 text-blue-500" />;
    }
  };

  const getBgColor = () => {
    switch (toast.type) {
      case 'success':
        return 'bg-green-50 border-green-200';
      case 'error':
        return 'bg-red-50 border-red-200';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200';
      case 'info':
      default:
        return 'bg-blue-50 border-blue-200';
    }
  };

  const getTextColor = () => {
    switch (toast.type) {
      case 'success':
        return 'text-green-800';
      case 'error':
        return 'text-red-800';
      case 'warning':
        return 'text-yellow-800';
      case 'info':
      default:
        return 'text-blue-800';
    }
  };

  // ANNOUNCED, not just drawn. Toast.js had no role and no aria-live anywhere, so every
  // toast in the app was silent to a screen reader -- a sighted user saw "settings saved"
  // and a screen-reader user got nothing at all. It was reported against Quote Acceptance,
  // but this is the only Toast component, so it was true of every confirmation, every
  // error and every warning in the product.
  //
  // Errors are assertive because they interrupt what the user is doing and usually mean an
  // action did not happen; everything else is polite so a success message does not cut
  // across whatever is being read. aria-atomic so the whole sentence is spoken rather than
  // just the part that changed.
  const isError = toast.type === 'error';
  return (
    <div
      role={isError ? 'alert' : 'status'}
      aria-live={isError ? 'assertive' : 'polite'}
      aria-atomic="true"
      className={`flex items-center gap-3 px-4 py-3 rounded-lg border ${getBgColor()} ${getTextColor()} shadow-lg animate-slide-in`}
    >
      {getIcon()}
      <span className="flex-1 text-sm font-medium">{toast.message}</span>
      <button
        onClick={onDismiss}
        aria-label="Dismiss notification"
        className="text-gray-400 hover:text-gray-600 flex-shrink-0"
      >
        <XMarkIcon className="w-4 h-4" aria-hidden="true" />
      </button>
    </div>
  );
};

export const ToastContainer = () => {
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    const unsubscribe = subscribeToToasts(setToasts);
    return unsubscribe;
  }, []);

  const dismissToast = (id) => {
    toastQueue = toastQueue.filter(t => t.id !== id);
    notifyToastListeners();
  };

  return (
    // #5093: bottom-20 (80px) clears the ReportProblemButton (fixed bottom-6, 48px tall = 72px)
    // at every width, so a save-success toast is no longer covered by it.
    <div className="fixed bottom-20 right-4 z-9999 space-y-2 pointer-events-none">
      {toasts.map(toast => (
        <div key={toast.id} className="pointer-events-auto">
          <Toast
            toast={toast}
            onDismiss={() => dismissToast(toast.id)}
          />
        </div>
      ))}
    </div>
  );
};

export default Toast;

